// resources/js/alpinejs/helpers/data-timestamp.js
export class TimestampFormatter {
    constructor() {
        this.formats = {
            default: {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            },
            short: {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            },
            long: {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            },
            date: {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            },
            time: {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            },
            relative: null // Special case for relative formatting
        };

        this.observer = null;
    }

    /**
     * Format a timestamp using specified format
     * @param {string} timestamp - ISO timestamp string
     * @param {string} formatName - Format name from this.formats
     * @returns {string} Formatted timestamp
     */
    format(timestamp, formatName = 'default') {
        if (!timestamp) return 'Unknown time';

        try {
            if (formatName === 'relative') {
                return this.formatRelative(timestamp);
            }

            const options = this.formats[formatName] || this.formats.default;
            return new Date(timestamp).toLocaleString(undefined, options);
        } catch (e) {
            console.warn('TimestampFormatter: Invalid timestamp:', timestamp, e);
            return 'Invalid date';
        }
    }

    /**
     * Format timestamp as relative time (e.g., "2 hours ago")
     * @param {string} timestamp - ISO timestamp string
     * @returns {string} Relative time string
     */
    formatRelative(timestamp) {
        if (!timestamp) return 'Unknown time';

        try {
            const now = new Date();
            const date = new Date(timestamp);
            if (Number.isNaN(date.getTime())) return 'Invalid date';
            const diffInSeconds = Math.floor((now - date) / 1000);

            // Future dates
            if (diffInSeconds < 0) {
                const absDiff = Math.abs(diffInSeconds);
                const intervals = [
                    { label: 'year', seconds: 31536000 },
                    { label: 'month', seconds: 2592000 },
                    { label: 'week', seconds: 604800 },
                    { label: 'day', seconds: 86400 },
                    { label: 'hour', seconds: 3600 },
                    { label: 'minute', seconds: 60 }
                ];

                for (const interval of intervals) {
                    const count = Math.floor(absDiff / interval.seconds);
                    if (count > 0) {
                        return `in ${count} ${interval.label}${count > 1 ? 's' : ''}`;
                    }
                }

                return 'in a moment';
            }

            // Past dates
            const intervals = [
                { label: 'year', seconds: 31536000 },
                { label: 'month', seconds: 2592000 },
                { label: 'week', seconds: 604800 },
                { label: 'day', seconds: 86400 },
                { label: 'hour', seconds: 3600 },
                { label: 'minute', seconds: 60 }
            ];

            for (const interval of intervals) {
                const count = Math.floor(diffInSeconds / interval.seconds);
                if (count > 0) {
                    return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
                }
            }

            return 'just now';
        } catch (e) {
            console.warn('TimestampFormatter: Error in formatRelative:', timestamp, e);
            return 'Unknown time';
        }
    }

    /**
     * Add a custom format
     * @param {string} name - Format name
     * @param {object} options - Intl.DateTimeFormat options
     */
    addFormat(name, options) {
        this.formats[name] = options;
    }

    /**
     * Format all timestamps found in the DOM
     * @param {Element} container - Container element to search within (defaults to document.body)
     */
    formatAll(container = document.body) {
        const timestamps = container.querySelectorAll('[data-timestamp]');

        timestamps.forEach(el => {
            const timestamp = el.getAttribute('data-timestamp');
            const format = el.getAttribute('data-timestamp-format') || 'default';
            const isRelative = el.hasAttribute('data-timestamp-relative');

            if (timestamp) {
                const formatToUse = isRelative ? 'relative' : format;
                const formatted = this.format(timestamp, formatToUse);

                // Update the text content
                el.textContent = formatted;

                // Add full timestamp as tooltip (unless it's already there)
                if (!el.getAttribute('title')) {
                    try {
                        const fullFormat = new Date(timestamp).toLocaleString(undefined, {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            second: '2-digit',
                            timeZoneName: 'short'
                        });
                        el.setAttribute('title', fullFormat);
                    } catch (e) {
                        // Fallback if full format fails
                        el.setAttribute('title', timestamp);
                    }
                }

                // Mark as processed to avoid re-processing
                el.setAttribute('data-timestamp-processed', 'true');
            }
        });
    }

    /**
     * Set up DOM observers to automatically format new timestamps
     */
    setupObserver() {
        if (this.observer) {
            this.observer.disconnect();
        }

        this.observer = new MutationObserver((mutations) => {
            let shouldFormat = false;

            mutations.forEach((mutation) => {
                // Check if any new nodes contain timestamps
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        const element = node;

                        // Check if the element itself has a timestamp
                        if (element.hasAttribute && element.hasAttribute('data-timestamp')) {
                            shouldFormat = true;
                        }

                        // Check if any children have timestamps
                        if (element.querySelector && element.querySelector('[data-timestamp]:not([data-timestamp-processed])')) {
                            shouldFormat = true;
                        }
                    }
                });
            });

            if (shouldFormat) {
                // Use requestAnimationFrame to batch DOM updates
                requestAnimationFrame(() => {
                    this.formatAll();
                });
            }
        });

        this.observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Initialize the timestamp formatter
     */
    init() {
        // Format existing timestamps when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.formatAll();
                this.setupObserver();
            });
        } else {
            this.formatAll();
            this.setupObserver();
        }

        // Re-format when language changes
        window.addEventListener('languagechange', () => {
            // Remove processed flags to force re-formatting
            document.querySelectorAll('[data-timestamp-processed]').forEach(el => {
                el.removeAttribute('data-timestamp-processed');
            });
            this.formatAll();
        });

        document.addEventListener('livewire:init', () => {
            window.Livewire.hook('commit', ({ succeed }) => {
                succeed(() => {
                    queueMicrotask(() => this.formatAll());
                });
            });
        });

    }

    /**
     * Destroy the timestamp formatter and clean up
     */
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }
}

// Create and export a singleton instance
export const timestampFormatter = new TimestampFormatter();

// Auto-initialize unless explicitly disabled
if (!window.DISABLE_AUTO_TIMESTAMP_FORMATTING) {
    timestampFormatter.init();
}
